import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import ErrorIcon from '~/meta/error.svg'
import { AppError, fromUnknownError } from '../../error'
import Loader from './loader'
import { useKeycloak } from '~/components/keycloak'
import keycloak from '~/utils/client/keycloak'
import { clear } from '~/prix/utils/browserCache'

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  align-items: center;

  .svg {
    display: flex;
  }
  .error-message {
    margin-bottom: 5px;
    margin-top: 20px;
    align-items: center;
    max-width: 1000px;
    small {
      font-size: 20px;
      color: ${({ theme }) => theme.colors.primary};
    }
    span {
      font-weight: bold;
      color: ${({ theme }) => theme.colors.darkGrey};
      font-size: 28px;
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const Button = styled.button`
  display: center;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 700;
  background-color: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: 4px;
  align-self: center;
  padding: 0.5rem 2rem;
  margin: 0.625rem;
  transition-duration: 0.4s;
  border: 1px solid transparent;
  cursor: pointer;
  span {
    font-size: 16px;
    color: #fff;
  }
  &:hover {
    color: #fff;
    background-color: ${({ theme }) => theme.colors.secondary};
    border: 1px solid #fff;
  }
`

const AccessLink = styled.a`
  display: block;
  margin-top: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};

  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
  }
`

export interface HandleProps extends React.HTMLAttributes<HTMLDivElement> {
  isLoading?: boolean
  error?: Error | AppError | null | undefined
  onTryAgain?: () => Promise<void> | void
  tryAgainButtonLabel?: string
  loginAction?: boolean
  children?: React.ReactNode
  stateRoles?: boolean
  onLogout?: () => Promise<void> | void
}

export default function Handle({
  isLoading = false,
  error = null,
  children = null,
  onTryAgain,
  tryAgainButtonLabel = 'Tentar novamente',
  loginAction,
  stateRoles,
  onLogout,
  ...props
}: HandleProps) {
  const { logout, keycloak: keycloakFromContext } = useKeycloak()

  const finalError = useMemo(() => {
    if (!error) {
      return null
    }
    return fromUnknownError(error)
  }, [error])
  const shouldShowErrorPrefix = useMemo(() => {
    if (!finalError) {
      return false
    }

    return finalError.message !== 'Não autenticado' && finalError.payload?.isAuthenticationRequired !== true
  }, [finalError])

  const handleLogout = useCallback(async () => {
    if (onLogout) {
      await onLogout()
      return
    }

    if (keycloakFromContext) {
      await logout().catch(error => console.error(error))
      return
    }

    await clear().catch(() => {})
    await keycloak.logout({ redirectUri: window.location.origin }).catch(console.error)
  }, [keycloakFromContext, logout, onLogout])

  const handleTryAgainClick = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault()
      if (finalError?.key === 'permission') {
        await handleLogout()
        return
      }

      if (!onTryAgain) {
        return
      }

      await onTryAgain()
    },
    [finalError?.key, handleLogout, onTryAgain],
  )

  if (isLoading) {
    return <Wrapper {...props}>Carregando...</Wrapper>
  }
  if (!finalError) {
    return children as React.ReactElement
  }

  return (
    <Wrapper {...props}>
      <div className='svg'>
        <ErrorIcon width={400} height={400} />
      </div>
      <div className='error-message'>
        {shouldShowErrorPrefix ? (
          <>
            <small>Ocorreu um erro</small>
            <br />
          </>
        ) : null}
        <span>{stateRoles ? error?.message : finalError.message}</span>
      </div>
      {onTryAgain && !stateRoles ? (
        <>
          <Button onClick={handleTryAgainClick}>{tryAgainButtonLabel}</Button>
          <AccessLink href='https://forms.office.com/r/K0K66VYKBV' target='_blank' rel='noreferrer'>
            Solicitar acesso
          </AccessLink>
        </>
      ) : null}
      {stateRoles ? <Button onClick={handleLogout}>Voltar ao login</Button> : null}
      {isLoading ? <Loader /> : null}
    </Wrapper>
  )
}
